import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { APIApplication } from 'types/Application'
import AdvancedTable from 'components/AdvancedTable'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import { useNavigate } from 'react-router'
import Typography from '@mui/material/Typography'
import { StatusTypes, applicationStatuses } from 'pages/Applications/applicationStatuses'
import { useAppContext } from 'context/AppContext/AppContext'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { axiosPatch } from 'connectors/axiosPatch'
import { Can } from 'config/ability'
import type { UpdateStatusData } from './Applications.types'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import { useAlert } from 'context/AlertContext'
import { useAuthContext } from 'context/AuthContext/AuthContext'
import { generateQueryParams } from 'utils/generateQueryParams'
import { Backdrop, CircularProgress } from '@mui/material'
import { axiosDelete } from 'connectors/axiosDelete'
import { renderStatusFilter } from './partials/renderStatusFilter'
import { Add } from '@mui/icons-material'
import { ExportIcon } from 'assets/icons/export'
import { ApplicationRow } from './partials/application-row'
import { fieldToSelect } from './config/fields-to-select'
import PageLoader from 'components/PageLoader'
import { renderMarketFilter } from './partials/renderMarketFilter'

enum FilterGroup {
  APPLICATION_DATA = 1,
  STATUS = 2,
}

const Applications = (): JSX.Element => {
  const queryClient = useQueryClient()
  const [isExportMode, setIsExportMode] = useState<boolean>(false)
  const [isExporting, setIsExporting] = useState<boolean>(false)
  const [elementToDelete, setElementToDelete] = useState<APIApplication | null>(null)
  const [elementToHardDelete, setElementToHardDelete] = useState<APIApplication | null>(null)
  const { changeMessage } = useAlert()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { markets } = useAppContext()
  const { token, refreshToken, get2fa } = useAuthContext()
  const [filtersParams, setFiltersParams] = useState<string>('')

  const {
    mutate: updateStatusMutate,
    data: updateStatusResponse,
    reset,
    isLoading: isStatusUpdating,
  } = useMutation(
    async ({ id, newStatusId }: UpdateStatusData) =>
      await axiosPatch<{ affected: number }>(token, refreshToken, `applications/status/${id}`, { status: newStatusId }),
    {
      onSuccess: response => {
        if (response !== undefined && !('error' in response)) {
          void queryClient.refetchQueries(['applications-query'])
        }
      },
    }
  )

  const {
    mutate: hardDelete,
    data: hardDeleteResponse,
    isLoading,
  } = useMutation(async (id: number) => await axiosDelete(token, refreshToken, `applications/${id}`), {
    onSuccess: response => {
      if (response !== undefined && !('error' in response)) {
        void queryClient.refetchQueries(['applications-query'])
      }
    },
    onSettled: data => {
      if (data && 'error' in data && 'message' in data.error?.data) {
        changeMessage(data?.error?.data?.message, 'error')
      }
    },
  })

  useEffect(() => {
    void queryClient.refetchQueries({ exact: false, queryKey: ['applications-query'] })
    if (
      typeof updateStatusResponse !== 'string' &&
      updateStatusResponse !== undefined &&
      'error' in updateStatusResponse
    ) {
      changeMessage(updateStatusResponse?.error?.data?.message, 'error', reset)
    }
    if (
      typeof updateStatusResponse !== 'string' &&
      updateStatusResponse !== undefined &&
      !('error' in updateStatusResponse) &&
      updateStatusResponse.affected > 0
    ) {
      changeMessage(t('common.success'), 'success', () => {
        reset()
      })
    }
  }, [updateStatusResponse])

  useEffect(() => {
    if (!isLoading) {
      setElementToHardDelete(null)
      void queryClient.refetchQueries({ exact: false, queryKey: ['applications-query'] })
    }
  }, [hardDeleteResponse, isLoading])

  const handleDownloadApplications = async (filterToDownload: Record<string, string | string[]>): Promise<any> => {
    setIsExporting(true)
    const params = generateQueryParams(filterToDownload)

    const url = `${process.env.REACT_APP_API_URL}/applications/export?${params.toString()}`

    const options: RequestInit = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      cache: 'no-cache',
    }

    const res = await fetch(url, options)
    if (res.ok) {
      const blob = await res.blob()
      const file = window.URL.createObjectURL(blob)
      window.location.assign(file)
      setIsExportMode(false)
      setIsExporting(false)
      void queryClient.refetchQueries(['applications-query'])
    } else {
      const data = await res.json()
      setIsExportMode(false)
      setIsExporting(false)
      if (data?.message !== undefined) {
        changeMessage(data.message, 'error')
      }
    }
  }

  const handleHideDialog = (): void => {
    setElementToDelete(null)
    setElementToHardDelete(null)
  }

  const handleDelete = (): void => {
    if (elementToDelete !== null) {
      updateStatusMutate({ id: elementToDelete.application_id, newStatusId: StatusTypes.Deleted })
      setElementToDelete(null)
    }
  }

  const changeExportMode = (value: boolean): void => {
    setIsExportMode(value)
  }

  const marketOptions = useMemo(
    () =>
      markets !== undefined && 'data' in markets
        ? markets.data
            .sort((a, b) => a.name.localeCompare(b.name))
            .filter(el => el.flow !== 'gateway')
            .map(el => ({
              id: el.id.toString(),
              value: (
                <Box>
                  {el.name}

                  {!!el.description && (
                    <Typography display="block" color="grey.400" variant="caption" lineHeight={1} fontWeight={400}>
                      {el.description}
                    </Typography>
                  )}
                </Box>
              ),
            }))
        : [],
    [markets]
  )

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography component="h1" variant="h5" fontWeight={600}>
            {t('navigation.applications')}
          </Typography>
          <Typography component="p" variant="body1" fontWeight={400} color="grey.600">
            {t('common.trackApplication')}
          </Typography>
        </Box>
        <Can I="create" a="Applications">
          <Button
            variant="outlined"
            color="secondary"
            sx={{ ml: 'auto', mr: 1 }}
            onClick={(): void => {
              navigate('/applications/add')
            }}
            startIcon={<Add />}
          >
            {t('common.add')}
          </Button>
        </Can>
        <Can I="download" a="Applications">
          <Button
            onClick={() => {
              setIsExportMode(true)
            }}
            type="button"
            variant="contained"
            startIcon={ExportIcon}
          >
            {t('common.exportList')}
          </Button>
        </Can>
      </Box>
      <AdvancedTable<APIApplication>
        name="applications"
        endpoint="applications"
        orderField="createdAt"
        orderDir="DESC"
        isExportMode={isExportMode}
        exportText={t('common.download')}
        changeExportMode={changeExportMode}
        columnsToExport={fieldToSelect.map(el => ({ ...el, label: t(el.field.replaceAll('_', '.')) }))}
        renderFilters={[
          {
            field: 'marketId',
            render: val => {
              if (Array.isArray(val)) {
                const value = marketOptions.filter(el => val.includes(el.id)).map(el => el.value)
                return renderMarketFilter(value)
              }
              return null
            },
          },
          {
            field: 'status',
            render: renderStatusFilter,
          },
        ]}
        onExport={async filters => {
          get2fa(
            async tfa => {
              await handleDownloadApplications({ ...filters, tfa })
            },
            () => {
              setIsExportMode(false)
            }
          )
        }}
        onUsedFilters={async filters => {
          const status = typeof filters.status === 'boolean' ? [filters.status.toString()] : (filters.status ?? [])
          const marketId =
            typeof filters.marketId === 'boolean' ? [filters.marketId.toString()] : (filters.marketId ?? [])
          setFiltersParams(generateQueryParams({ status, marketId }).toString())
        }}
        customFilters={[
          { type: 'text', group: FilterGroup.APPLICATION_DATA, name: 'Email', field: 'email', sort: false, order: 2 },
        ]}
        filterGroups={[
          { id: FilterGroup.APPLICATION_DATA, label: t('applications.applicationsDataFilterGroup') },
          { id: FilterGroup.STATUS, label: t('applications.statusFilterGroup') },
        ]}
        headings={[
          {
            type: 'text',
            group: FilterGroup.APPLICATION_DATA,
            name: 'ID',
            field: 'id',
            sort: true,
            width: 90,
            order: 1,
          },
          {
            type: 'text',
            group: FilterGroup.APPLICATION_DATA,
            name: t('common.customer'),
            field: 'fullName',
            sort: true,
            width: 220,
            order: 3,
          },
          {
            type: 'multipicker',
            group: FilterGroup.APPLICATION_DATA,
            name: t('common.marketName'),
            field: 'marketId',
            width: 170,
            order: 5,
          },
          {
            group: FilterGroup.APPLICATION_DATA,
            type: 'datetime-range',
            name: t('common.createdAt'),
            field: 'createdAt',
            sort: true,
            width: 140,
            order: 3,
          },
          {
            group: FilterGroup.APPLICATION_DATA,
            type: 'datetime-range',
            name: `${t('common.createdAtLocal')}`,
            field: 'createdAtLocal',
            sort: true,
            width: 140,
            order: 3,
          },
          {
            group: FilterGroup.STATUS,
            type: 'multipicker',
            name: t('application.applicationStatus'),
            field: 'status',
            sort: true,
            width: 120,
            order: 5,
          },
          { type: 'empty', name: '', field: '', width: 90, order: 4 },
        ]}
        options={{
          marketId: marketOptions,
          status: applicationStatuses.map((status, idx) => ({ id: idx.toString(), value: status.value })),
        }}
        renderRow={(row, idx) => (
          <ApplicationRow
            key={`application-${idx}`}
            {...row}
            filtersParams={filtersParams}
            setElementToDelete={setElementToDelete}
            setElementToHardDelete={setElementToHardDelete}
          />
        )}
      />
      {elementToDelete !== null ? (
        <ConfirmDialog
          open={elementToDelete !== null}
          onAgree={handleDelete}
          onDisagree={handleHideDialog}
          question={t('dialogs.delete')}
        />
      ) : null}
      {elementToHardDelete !== null ? (
        <ConfirmDialog
          open={elementToHardDelete !== null}
          onAgree={() => {
            hardDelete(elementToHardDelete.application_id)
          }}
          onDisagree={handleHideDialog}
          question={t('dialogs.delete')}
        />
      ) : null}
      <Backdrop sx={{ color: '#fff', zIndex: theme => theme.zIndex.modal + 1 }} open={isExporting}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <CircularProgress color="warning" />
          <Typography fontFamily="inherit" fontWeight={800}>
            {t('application.exporting')}
          </Typography>
        </Box>
      </Backdrop>
      {(isLoading || isStatusUpdating) && <PageLoader />}
    </>
  )
}

export default Applications
